import React from "react";
import starIcon from "@/assets/icons/star.svg";
import volumeUp from "@/assets/icons/volume-up.svg";
import Bulb from "@/assets/icons/bulb.svg";
import pencil from "@/assets/icons/pencil.svg";
import Image from "next/image";
import { cn } from "@/lib/utils";

// Props interface for the Flashcard component
type FlashcardProps = {
  hint: string; // Hint text displayed below the question
  question: string; // Main question text
  subject: string; // Subject tag shown in footer
  color: string; // Background color of the card
  subjectColor: string; // Background color of the subject tag
};

const Flashcard = ({
  hint,
  question,
  color,
  subject,
  subjectColor,
}: FlashcardProps) => {
  return (
    <>
      {/* Flashcard container with responsive sizing (20% smaller than original) */}
      <div
        className={`w-[11rem] sm:w-[13.4rem] md:w-[13.5rem] lg:w-[14.4rem] xl:w-[15.3rem] max-w-[15.3rem] 
                   h-[12rem] sm:h-[15rem] md:h-[15.3rem] lg:h-[16.2rem] xl:h-[17.1rem] max-h-[17.1rem] 
                   py-[0.6rem] sm:py-[0.8rem] md:py-[1rem] 
                   px-[0.6rem] sm:px-[0.8rem] md:px-[1rem]
                   rounded-[1.2rem] sm:rounded-[1.2rem] md:rounded-[1.4rem] 
                   border-[0.03125rem] border-dashed border-black relative
                    duration-300`}
        style={{ backgroundColor: color }}
      >
        {/* Header section with question label and interactive icons */}
        <div className="flex flex-col gap-[0.2rem] sm:gap-[0.4rem]">
          {/* Question label with scaled-down text */}
          <p className="text-[0.55rem] sm:text-[0.6rem] font-poppins">
            Question
          </p>
          {/* Question text with responsive sizing and line clamping */}
          <p className="font-editorial text-[0.65rem] sm:text-[0.7rem] md:text-[0.8rem] font-bold tracking-[-0.015em] line-clamp-3">
            {question}
          </p>
          {/* Interactive icons container */}
          <div className="flex items-center gap-[0.3rem] sm:gap-[0.2rem]">
            {/* Audio playback icon - scaled for mobile */}
            <div className="w-[0.7rem] sm:w-[1rem] h-[0.7rem] sm:h-[1rem] relative">
              <Image
                src={volumeUp}
                alt="speaker icon"
                fill
                className="object-contain"
              />
            </div>
            {/* Favorite/star icon - scaled for mobile */}
            <div className="w-[0.7rem] sm:w-[1rem] h-[0.7rem] sm:h-[1rem] relative">
              <Image
                src={starIcon}
                alt="star icon"
                fill
                className="object-contain"
              />
            </div>
          </div>
        </div>

        {/* Divider line with consistent spacing */}
        <div className="border-b-[0.03125rem] border-dashed border-black mt-[0.4rem] sm:mt-[0.8rem] md:mt-[1rem] -mx-[0.6rem] sm:-mx-[0.8rem] md:-mx-[1rem]" />

        {/* Content section containing hint */}
        <div className="mt-[0.3rem] sm:mt-[0.8rem] md:mt-[1rem] flex flex-col gap-[0.2rem]">
          {/* Hint label with icon */}
          <div className="flex gap-[0.1rem] items-center">
            {/* Hint bulb icon - scaled for mobile */}
            <div className="w-[0.7rem] sm:w-[1rem] h-[0.7rem] sm:h-[1rem] relative">
              <Image
                src={Bulb}
                alt="bulb icon"
                fill
                className="object-contain"
              />
            </div>
            {/* Hint label text */}
            <p className="text-[0.55rem] sm:text-[0.6rem] font-editorial font-bold tracking-tighter">
              Hint
            </p>
          </div>
          {/* Hint content with line clamping */}
          <div className="font-poppins font-medium text-[0.55rem] sm:text-[0.6rem] md:text-[0.7rem] line-clamp-2">
            {hint}
          </div>
        </div>

        {/* Footer subject tag with custom background */}
        <div
          className={cn(
            `absolute flex gap-[0.1rem] sm:gap-[0.2rem] items-center bottom-[0.2rem] sm:bottom-[0.4rem] right-[0.4rem] sm:right-[0.8rem] 
             w-fit pl-[0.1rem] sm:pl-[0.3rem] pr-[0.2rem] sm:pr-[0.5rem] rounded-[0.4rem] sm:rounded-[1.2rem] py-[0.1rem]`
          )}
          style={{ backgroundColor: subjectColor }}
        >
          {/* Subject icon - scaled for mobile */}
          <div className="w-[0.3rem] sm:w-[0.8rem] h-[0.3rem] sm:h-[0.8rem] relative">
            <Image
              src={pencil}
              alt="pencil icon"
              fill
              className="object-contain"
            />
          </div>
          {/* Subject text with smallest text size */}
          <span className="text-[0.45rem] sm:text-[0.5rem] md:text-[0.5rem] tracking-[-0.0125em] font-semibold">
            {subject}
          </span>
        </div>
      </div>
    </>
  );
};

export default Flashcard;
